import { CalendarClock } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { CategoryIcon } from "@/components/CategoryIcon";
import { Button } from "@/components/ui/button";
import { Card, PageHeader } from "@/components/ui/card";
import { EmptyState, ErrorBanner, Loading } from "@/components/ui/feedback";
import { Select, TextInput } from "@/components/ui/field";
import {
  useAccounts,
  useCategories,
  useCreateRecurrence,
  useDeleteRecurrence,
  useRecurrences,
} from "../../api/finance";
import type { CategoryKind, Recurrence } from "../../api/types";
import { Amount } from "../../components/Amount";
import { MoneyInput } from "../../components/MoneyInput";
import { useCan, useWorkspace } from "../../layout/workspace-context";
import { formatDate, shortDay, todayCivil, WEEKDAYS } from "../../lib/dates";
import { parseMoneyInput } from "../../lib/money";
import { nextOccurrence } from "../../lib/recurrence";

const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

export function describeRule(r: Pick<Recurrence, "frequency" | "dayOfMonth" | "weekday" | "month">) {
  if (r.frequency === "weekly") {
    const day = WEEKDAYS[r.weekday ?? 0] ?? "";
    return `Semanal, ${r.weekday === 0 || r.weekday === 6 ? "todo" : "toda"} ${day}`;
  }
  if (r.frequency === "yearly") return `Anual, todo ${r.dayOfMonth} de ${MONTHS[(r.month ?? 1) - 1]}`;
  return `Mensal, todo dia ${r.dayOfMonth}`;
}

export function RecurrencesPage() {
  const ws = useWorkspace();
  const canEdit = useCan("catalog:write");
  const recurrences = useRecurrences(ws.id);
  // Contas arquivadas também: a recorrência mantém a moeda da conta de origem.
  const accounts = useAccounts(ws.id, true);
  const categories = useCategories(ws.id);
  const remove = useDeleteRecurrence(ws.id);
  const today = todayCivil(ws.timezone);
  const currencyOf = (accountId: string) =>
    accounts.data?.find((a) => a.id === accountId)?.currency ?? ws.defaultCurrency;

  return (
    <>
      <PageHeader title="Recorrências" />
      <p className="m-0 mb-4 text-sm text-muted">
        Lançamentos que se repetem (aluguel, salário, assinaturas) são criados automaticamente como pendentes
        na data.
      </p>
      {recurrences.isPending ? (
        <Loading />
      ) : recurrences.isError ? (
        <ErrorBanner error={recurrences.error} />
      ) : recurrences.data.length === 0 ? (
        <EmptyState title="Nenhuma recorrência." />
      ) : (
        <ul
          aria-label="Recorrências"
          className="m-0 mb-8 list-none rounded-card bg-surface p-0 px-5 shadow-card"
        >
          {[...recurrences.data]
            .map((r) => ({ r, next: nextOccurrence(r, today) }))
            .sort((a, b) => (a.next ?? "9999").localeCompare(b.next ?? "9999"))
            .map(({ r, next }) => (
              <li
                key={r.id}
                aria-label={r.description}
                className="flex items-center gap-3 border-b border-border py-3 last:border-0"
              >
                <CategoryIcon category={categories.data?.find((c) => c.id === r.categoryId) ?? null} />
                <div className="min-w-0 flex-1">
                  <p className="m-0 truncate font-medium">{r.description}</p>
                  <p className="m-0 text-xs text-muted">
                    {describeRule(r)}
                    {r.endDate && ` · até ${formatDate(r.endDate)}`}
                  </p>
                  <p className="m-0 mt-0.5 flex items-center gap-1 text-xs font-medium text-ink">
                    <CalendarClock aria-hidden="true" className="size-3.5 text-muted" />
                    {next ? `Próxima: ${shortDay(next)}` : "Sem próximas datas"}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <Amount
                    minor={r.amountMinor}
                    currency={currencyOf(r.accountId)}
                    signed={r.type === "income" ? "in" : "out"}
                  />
                  {canEdit && (
                    <Button
                      variant="ghost"
                      className="min-h-9 px-2 text-sm"
                      aria-label={`Encerrar ${r.description}`}
                      onClick={() => remove.mutate(r.id)}
                    >
                      Encerrar
                    </Button>
                  )}
                </div>
              </li>
            ))}
        </ul>
      )}
      <ErrorBanner error={remove.error} />
      {canEdit && <RecurrenceForm />}
    </>
  );
}

function RecurrenceForm() {
  const ws = useWorkspace();
  const accounts = useAccounts(ws.id);
  const categories = useCategories(ws.id);
  const create = useCreateRecurrence(ws.id);
  const today = todayCivil(ws.timezone);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [type, setType] = useState<CategoryKind>("expense");
  const [frequency, setFrequency] = useState<Recurrence["frequency"]>("monthly");
  const [dayOfMonth, setDayOfMonth] = useState(String(Number(today.slice(8, 10))));
  const [weekday, setWeekday] = useState(String(new Date(`${today}T12:00:00Z`).getUTCDay()));
  const [month, setMonth] = useState(String(Number(today.slice(5, 7))));
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState("");
  const [accountId, setAccountId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [invalid, setInvalid] = useState(false);

  const options = (categories.data ?? []).filter((c) => c.kind === type && !c.archivedAt);
  useEffect(() => {
    if (!accountId && accounts.data?.[0]) setAccountId(accounts.data[0].id);
  }, [accounts.data, accountId]);
  useEffect(() => {
    if (!options.some((o) => o.id === categoryId) && options[0]) setCategoryId(options[0].id);
  }, [options, categoryId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const amountMinor = parseMoneyInput(amount);
    const day = Number(dayOfMonth);
    if (amountMinor === null || (frequency !== "weekly" && !(day >= 1 && day <= 31))) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    await create.mutateAsync({
      description: description.trim(),
      amountMinor,
      type,
      frequency,
      ...(frequency === "weekly" ? { weekday: Number(weekday) } : { dayOfMonth: day }),
      ...(frequency === "yearly" ? { month: Number(month) } : {}),
      startDate,
      ...(endDate ? { endDate } : {}),
      accountId,
      categoryId,
    });
    setDescription("");
    setAmount("");
  }

  return (
    <Card title="Nova recorrência" className="max-w-xl">
      <form onSubmit={(e) => void submit(e).catch(() => undefined)} className="flex flex-col gap-4">
        <TextInput
          label="Descrição"
          required
          maxLength={200}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <MoneyInput label="Valor" required value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Select label="Tipo" value={type} onChange={(e) => setType(e.target.value as CategoryKind)}>
          <option value="expense">Despesa</option>
          <option value="income">Receita</option>
        </Select>
        <Select
          label="Frequência"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value as Recurrence["frequency"])}
        >
          <option value="monthly">Mensal</option>
          <option value="weekly">Semanal</option>
          <option value="yearly">Anual</option>
        </Select>
        {frequency === "weekly" ? (
          <Select label="Dia da semana" value={weekday} onChange={(e) => setWeekday(e.target.value)}>
            {WEEKDAYS.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </Select>
        ) : (
          <TextInput
            label="Dia do mês"
            type="number"
            min={1}
            max={31}
            hint="Em meses mais curtos, usa o último dia."
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(e.target.value)}
          />
        )}
        {frequency === "yearly" && (
          <Select label="Mês" value={month} onChange={(e) => setMonth(e.target.value)}>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </Select>
        )}
        <TextInput
          label="Início"
          type="date"
          required
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextInput
          label="Fim (opcional)"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <Select label="Conta" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          {accounts.data?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <Select label="Categoria" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {options.map((c) => (
            <option key={c.id} value={c.id}>
              {c.parentId ? `— ${c.name}` : c.name}
            </option>
          ))}
        </Select>
        {invalid && <p className="m-0 text-sm text-expense">Confira o valor e o dia.</p>}
        <ErrorBanner error={create.error} />
        <Button type="submit" disabled={create.isPending}>
          Criar recorrência
        </Button>
      </form>
    </Card>
  );
}
